import React, { useState, useEffect } from "react";
import api from '../services/api';
import "bootstrap/dist/css/bootstrap.min.css";
import { ColorBox } from "devextreme-react/color-box";
import CheckBox from "devextreme-react/check-box";
import countries from "../data/countries";
import { useAgents } from "../context/AgentContext";
import { useNavigate } from "react-router-dom";
import SelectBox from 'devextreme-react/select-box';
import { TextBox } from 'devextreme-react/text-box';
import { Link } from 'react-router-dom';

const DefaultSetting = () => {
  const [cvlayouts, setCvlayouts] = useState([]);
  const agents = useAgents() || [];
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log("Fetching data...");
        setError(null);
        
        // Fetch templates and settings separately to handle errors better
        let templates = [];
        let defaultData = {};
        
        try {
          const templatesResponse = await api.get("/CVTemplates");
          console.log("Templates response status:", templatesResponse.status);
          console.log("Templates response data:", templatesResponse.data);
          
          // Handle different response formats
          if (Array.isArray(templatesResponse.data)) {
            templates = templatesResponse.data;
          } else if (templatesResponse.data && Array.isArray(templatesResponse.data.$values)) {
            // Handle JSON serialization format
            templates = templatesResponse.data.$values;
          } else if (templatesResponse.data && typeof templatesResponse.data === 'object') {
            // Try to find array property
            const possibleArrays = Object.values(templatesResponse.data).filter(val => Array.isArray(val));
            if (possibleArrays.length > 0) {
              templates = possibleArrays[0];
            }
          }
          
          console.log("Processed templates:", templates);
          console.log("Templates count:", templates.length);
          
        } catch (templatesError) {
          console.error("Error fetching templates:", templatesError);
          // Try alternative endpoint if needed
          try {
            console.log("Trying alternative endpoint...");
            const altResponse = await api.get("/api/CVTemplates");
            if (Array.isArray(altResponse.data)) {
              templates = altResponse.data;
            }
          } catch (altError) {
            console.error("Alternative endpoint also failed:", altError);
          }
        }
        
        try {
          const settingsResponse = await api.get("/DefaultSettings");
          console.log("Settings response:", settingsResponse.data);
          defaultData = settingsResponse.data || {};
        } catch (settingsError) {
          console.error("Error fetching settings:", settingsError);
          // Continue with empty settings
        }
        
        setCvlayouts(templates);
        
        // Determine cvLayout - use first template if no default is set
        let cvLayoutValue = defaultData?.cvLayout || null;
        console.log("Default cvLayout from settings:", cvLayoutValue);
        
        // If no cvLayout is set and we have templates, use the first one
        if (!cvLayoutValue && templates.length > 0) {
          cvLayoutValue = templates[0].templateName;
          console.log("Using first template as default:", cvLayoutValue);
        }
        
        const newSettings = {
          cvColor: defaultData?.cvColor || "#ffffff",
          cvFontColor: defaultData?.cvFontColor || "#000000",
          cvLayout: cvLayoutValue,
          pageSize: defaultData?.pageSize || "ALL",
          showLetterDate: defaultData?.showLetterDate || false,
          enjazNoLength: defaultData?.enjazNoLength || 0,
          visaNoLength: defaultData?.visaNoLength || 0,
          passportNoLength: defaultData?.passportNoLength || 0,
          sponsorIDLength: defaultData?.sponsorIDLength || 0,
          passportExpiryMinDays: defaultData?.passportExpiryMinDays || 0,
          medicalExpiryMinDays: defaultData?.medicalExpiryMinDays || 0,
          minAge: defaultData?.minAge || 0,
          placeOfIssue: defaultData?.placeOfIssue || "",
          gender: defaultData?.gender || "",
          femaleSalary: defaultData?.femaleSalary || 0,
          femaleSalaryExp: defaultData?.femaleSalaryExp || 0,
          maleSalary: defaultData?.maleSalary || 0,
          passportType: defaultData?.passportType || "",
          religion: defaultData?.religion || "",
          maritalStatus: defaultData?.maritalStatus || "",
          currentNationality: defaultData?.currentNationality || "Ethiopia",
          qualification: defaultData?.qualification || "",
          femaleOccupation: defaultData?.femaleOccupation || "",
          maleOccupation: defaultData?.maleOccupation || "",
          visaType: defaultData?.visaType || "",
          sponsorAddress: defaultData?.sponsorAddress || "",
          witness1FullName: defaultData?.witness1FullName || "",
          witness2FullName: defaultData?.witness2FullName || "",
          generalManager: defaultData?.generalManager || "",
          gmTitle: defaultData?.gmTitle || "",
          witness1Address: defaultData?.witness1Address || "",
          witness2Address: defaultData?.witness2Address || "",
          witness1PhoneNo: defaultData?.witness1PhoneNo || "",
          witness2PhoneNo: defaultData?.witness2PhoneNo || "",
          displayContractDate: defaultData?.displayContractDate || "",
          officeAbroad: defaultData?.officeAbroad || "",
          displayUser: defaultData?.displayUser || false,
          displayBroker: defaultData?.displayBroker || false,
        };
        
        console.log("Settings being set:", newSettings);
        setSettings(newSettings);
        
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load data. Please try again.");
        setCvlayouts([]);
        setSettings({
          cvColor: "#ffffff",
          cvFontColor: "#000000",
          cvLayout: null,
          pageSize: "ALL",
          showLetterDate: false,
          enjazNoLength: 0,
          visaNoLength: 0,
          passportNoLength: 0,
          sponsorIDLength: 0,
          passportExpiryMinDays: 0,
          medicalExpiryMinDays: 0,
          minAge: 0,
          placeOfIssue: "",
          gender: "",
          femaleSalary: 0,
          femaleSalaryExp: 0,
          maleSalary: 0,
          passportType: "",
          religion: "",
          maritalStatus: "",
          currentNationality: "Ethiopia",
          qualification: "",
          femaleOccupation: "",
          maleOccupation: "",
          visaType: "",
          sponsorAddress: "",
          witness1FullName: "",
          witness2FullName: "",
          generalManager: "",
          gmTitle: "",
          witness1Address: "",
          witness2Address: "",
          witness1PhoneNo: "",
          witness2PhoneNo: "",
          displayContractDate: "",
          officeAbroad: "",
          displayUser: false,
          displayBroker: false,
        });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  if (isLoading) {
    return <div className="container mt-4">Loading...</div>;
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  if (!settings || !agents) {
    return <div className="container mt-4">Loading...</div>;
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings((prevSettings) => ({
      ...prevSettings,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleChecked = (field, value) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [field]: value,
    }));
  };

  const handleSettingChange = (field, value) => {
    setSettings((prevSettings) => ({
      ...prevSettings,
      [field]: value,
    }));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target || e;
    setSettings((prevSettings) => ({ ...prevSettings, [name]: value }));
  };

  const handleDxSelect = (e) => {
    console.log("SelectBox value changed:", e.value);
    setSettings(prev => ({ ...prev, cvLayout: e.value }));
  };

  const isValidGuid = (guid) => {
    const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    return guidRegex.test(guid);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
        // Map frontend property names to backend property names
        const payload = {
            ...settings,
            // Map gmTitle to GMTitle for backend
            GMTitle: settings.gmTitle,
            officeAbroad: isValidGuid(settings.officeAbroad) 
                ? settings.officeAbroad 
                : "00000000-0000-0000-0000-000000000000"
        };
        
        // Remove the frontend property name if it doesn't match backend
        delete payload.gmTitle;
  
        const response = await api.post('/DefaultSettings', payload);
        console.log("Save response:", response.data);
  
        navigate("/applicants/create", {
            state: { 
                success: true,
                message: response.data.message || "Default settings saved successfully"
            }
        });
  
    } catch (error) {
        console.error("Error saving default settings:", {
            message: error.message,
            status: error.response?.status,
            data: error.response?.data
        });
        
        const errorMessage = error.response?.data?.message 
            || error.response?.data?.error
            || error.message 
            || 'Failed to save default settings';
            
        setError(errorMessage);
    }
};

  // Log current state for debugging
  console.log("Rendering with cvlayouts:", cvlayouts);
  console.log("cvlayouts length:", cvlayouts.length);
  console.log("settings.cvLayout:", settings.cvLayout);

  return (
    <div className="container mt-4" style={{ maxWidth: "1100px" }}>
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="es-label-title">Company Settings</h5>
        {typeof window !== 'undefined' && (
          window.navigator.userAgent.includes("Firefox") ? (
            <a 
              href="https://addons.mozilla.org/firefox/downloads/file/4481143/ee2me-1.0.4.2.xpi" 
              className="es-link"
            >
              Configure
            </a>
          ) : (
            <a 
              href="https://chromewebstore.google.com/detail/ee2me/nkepfbjbmkcbbjhndocoadjflenjnchh?authuser=0&hl=en" 
              className="es-link"
            >
              Configure
            </a>
          )
        )}
      </div>

      <div className="card p-3">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* CV-COLOR and CV-Font-Color */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">CV-COLOR</label>
                <div className="col-sm-8">
                  <ColorBox
                    value={settings.cvColor}
                    onValueChanged={(e) => handleSettingChange("cvColor", e.value)}
                    applyValueMode="instantly"
                    editAlphaChannel={false}
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">CV-Font-Color</label>
                <div className="col-sm-8">
                  <ColorBox
                    value={settings.cvFontColor}
                    onValueChanged={(e) => handleSettingChange("cvFontColor", e.value)}
                    applyValueMode="instantly"
                    editAlphaChannel={false}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* CV-Layout */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">CV-Layout</label>
                <div className="col-sm-8">
                  {cvlayouts && cvlayouts.length > 0 ? (
                    <SelectBox
                      name="cvLayout"
                      dataSource={cvlayouts}
                      value={settings.cvLayout}
                      onValueChanged={handleDxSelect}
                      displayExpr="templateName"
                      valueExpr="templateName"
                      placeholder="Select a layout"
                      showClearButton={true}
                      itemRender={(item) => (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <img 
                            src={item.imageUrl?.startsWith('~') ? item.imageUrl.substring(1) : item.imageUrl}
                            alt={item.templateName}
                            style={{ width: '250px', height: '350px', marginRight: '10px', objectFit: 'contain' }}
                          />
                          <span>{item.templateName}</span>
                        </div>
                      )}
                      fieldRender={(selectedItem) => (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {selectedItem && (
                            <>
                              <img 
                                src={selectedItem.imageUrl?.startsWith('~') ? selectedItem.imageUrl.substring(1) : selectedItem.imageUrl} 
                                alt={selectedItem.templateName} 
                                style={{ width: '30px', height: '30px', marginRight: '10px', objectFit: 'contain' }}
                              />
                              <TextBox
                                readOnly={true}
                                value={selectedItem.templateName}
                                elementAttr={{ class: 'dx-selectbox-input' }}
                              />
                            </>
                          )}
                          {!selectedItem && (
                            <TextBox
                              readOnly={true}
                              placeholder="Select a layout"
                              elementAttr={{ class: 'dx-selectbox-input' }}
                            />
                          )}
                        </div>
                      )}
                    />
                  ) : (
                    <div className="alert alert-warning">
                      No CV templates available. Please add templates in the system.
                      <br />
                      <small className="text-muted">
                        API endpoint: /CVTemplates returned empty or no data.
                      </small>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label"></label>
                <Link to="/cvpreferences/create" className="col-sm-8 es-link">
                  Manage Agents CV Layout
                </Link>
              </div>
            </div>
          </div>

          {/* Display Letter Date and Enjaz Length */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <CheckBox 
                  className="es-checkbox es-field-label" 
                  value={settings.showLetterDate} 
                  onValueChanged={(e) => handleChecked("showLetterDate", e.value)} 
                  text="Display.Letter.Date" 
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Enjaz # Length</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    name="enjazNoLength"
                    value={settings.enjazNoLength}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ... rest of the form fields remain the same ... */}
          {/* Visa Length and Passport Length */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Visa # Length</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    name="visaNoLength"
                    value={settings.visaNoLength}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Passport # Length</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    name="passportNoLength"
                    value={settings.passportNoLength}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sponsor ID and Passport Expiry */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Sponsor ID Length</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    name="sponsorIDLength"
                    value={settings.sponsorIDLength}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Min Passport Expiry</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    name="passportExpiryMinDays"
                    value={settings.passportExpiryMinDays}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Medical Expiry and Min Age */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Min Medical Expiry</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    name="medicalExpiryMinDays"
                    value={settings.medicalExpiryMinDays}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Min Age</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    name="minAge"
                    value={settings.minAge}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Place of Issue and Gender */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Place of Issue</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="placeOfIssue"
                    value={settings.placeOfIssue}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Gender</label>
                <div className="col-sm-8">
                  <select
                    name="gender"
                    value={settings.gender}
                    onChange={handleSelectChange}
                    className="form-control"
                  >
                    <option value=""></option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Female Salary and Female Salary Exp */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Female Salary</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    name="femaleSalary"
                    value={settings.femaleSalary}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Female Salary Exp</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    name="femaleSalaryExp"
                    value={settings.femaleSalaryExp}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Male Salary and Passport Type */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Male Salary</label>
                <div className="col-sm-8">
                  <input
                    type="number"
                    name="maleSalary"
                    value={settings.maleSalary}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Passport Type</label>
                <div className="col-sm-8">
                  <select 
                    name="passportType" 
                    value={settings.passportType} 
                    onChange={handleSelectChange} 
                    className="form-control select-with-arrow"
                  >
                    <option value=""></option>
                    <option value="Normal">Normal</option>
                    <option value="Diplomatic">Diplomatic</option>
                    <option value="Special">Special</option>
                    <option value="Haji Cert">Haji Cert</option>
                    <option value="Travel Cert">Travel Cert</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Religion and Marital Status */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Religion</label>
                <div className="col-sm-8">
                  <select 
                    name="religion" 
                    value={settings.religion} 
                    onChange={handleSelectChange} 
                    className="form-control select-with-arrow"
                  >
                    <option value=""></option>
                    <option value="Islam">Islam</option>
                    <option value="Christian">Christian</option>
                    <option value="Buddhism">Buddhism</option>
                    <option value="Hinduism">Hinduism</option>
                    <option value="Sikhism">Sikhism</option>
                    <option value="Non-Muslim">Non-Muslim</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Marital Status</label>
                <div className="col-sm-8">
                  <select 
                    name="maritalStatus" 
                    value={settings.maritalStatus} 
                    onChange={handleSelectChange} 
                    className="form-control select-with-arrow"
                  >
                    <option value=""></option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widow">Widow</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Current Nationality and Qualification */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Current Nationality</label>
                <div className="col-sm-8">
                  <select 
                    name="currentNationality" 
                    value={settings.currentNationality} 
                    onChange={handleSelectChange} 
                    className="form-control select-with-arrow"
                  >
                    <option value="Ethiopia">Ethiopia</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Qualification</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="qualification"
                    value={settings.qualification}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Female Occupation and Male Occupation */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Female Occupation</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="femaleOccupation"
                    value={settings.femaleOccupation}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Male Occupation</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="maleOccupation"
                    value={settings.maleOccupation}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Visa Type and Sponsor Address */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Visa Type</label>
                <div className="col-sm-8">
                  <select 
                    name="visaType" 
                    value={settings.visaType} 
                    onChange={handleSelectChange} 
                    className="form-control select-with-arrow"
                  >
                    <option value="Business Visit">Business Visit</option>
                    <option value="Commercial Visit">Commercial Visit</option>
                    <option value="Diplomatic">Diplomatic</option>
                    <option value="Educational">Educational</option>
                    <option value="Escort Visa">Escort Visa</option>
                    <option value="Event Visa">Event Visa</option>
                    <option value="Extend Return">Extend Return</option>
                    <option value="Family Visit">Family Visit</option>
                    <option value="Government Visit">Government Visit</option>
                    <option value="Hajj">Hajj</option>
                    <option value="Medical">Medical</option>
                    <option value="Merchandise Delivery">Merchandise Delivery</option>
                    <option value="Missions and Organizations Visit">Missions and Organizations Visit</option>
                    <option value="Personal Visit">Personal Visit</option>
                    <option value="Resident">Resident</option>
                    <option value="Seasonal Work">Seasonal Work</option>
                    <option value="Special">Special</option>
                    <option value="Temporary work">Temporary work</option>
                    <option value="Tourism">Tourism</option>
                    <option value="Transit">Transit</option>
                    <option value="Umrah">Umrah</option>
                    <option value="Work">Work</option>
                    <option value="Working Visit">Working Visit</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Sponsor Address</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="sponsorAddress"
                    value={settings.sponsorAddress}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Witness 1 and 2 Full Names */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Witness 1 Full Name</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="witness1FullName"
                    value={settings.witness1FullName}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Witness 2 Full Name</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="witness2FullName"
                    value={settings.witness2FullName}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* General Manager and GM Title */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">General Manager</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="generalManager"
                    value={settings.generalManager}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">GM Title</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="gmTitle"
                    value={settings.gmTitle}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Witness 1 and 2 Addresses */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Witness 1 Address</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="witness1Address"
                    value={settings.witness1Address}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Witness 2 Address</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="witness2Address"
                    value={settings.witness2Address}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Witness 1 and 2 Phone Numbers */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Witness 1 Phone No</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="witness1PhoneNo"
                    value={settings.witness1PhoneNo}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Witness 2 Phone No</label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    name="witness2PhoneNo"
                    value={settings.witness2PhoneNo}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Display Contract Date and Agent */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Display Contract Date</label>
                <div className="col-sm-8">
                  <select 
                    name="displayContractDate" 
                    value={settings.displayContractDate} 
                    onChange={handleSelectChange} 
                    className="form-control select-with-arrow"
                  >
                    <option value=""></option>
                    <option value="DATE">DATE</option>
                    <option value="YEAR">YEAR</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label es-field-label">Agent</label>
                <div className="col-sm-8">
                  <select 
                    name="officeAbroad" 
                    value={settings.officeAbroad} 
                    onChange={handleSelectChange} 
                    className="form-control select-with-arrow"
                  >
                    <option value="">Select...</option>
                    {agents.map(company => (
                      <option key={company.companyID} value={company.companyID}>
                        {company.companyName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Display User and Display Broker */}
          <div className="row mb-1">
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label"></label>
                <div className="form-check col-sm-8">
                  <CheckBox 
                    className="es-checkbox es-field-label" 
                    value={settings.displayUser} 
                    onValueChanged={(e) => handleChecked("displayUser", e.value)} 
                    text="Display User" 
                  />
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="row">
                <label className="col-sm-4 col-form-label"></label>
                <div className="form-check col-sm-8">
                  <CheckBox 
                    className="es-checkbox es-field-label" 
                    value={settings.displayBroker} 
                    onValueChanged={(e) => handleChecked("displayBroker", e.value)} 
                    text="Display Broker" 
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="d-flex justify-content-end">
            <button type="submit" className="btn es-button">
              SAVE
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DefaultSetting;